import { StickyState, TextLabelState } from '../../../api/Requests';
import { DEFAULT_LABEL_FONT } from '../../../utils/stickyResize';
import { findZoneForPoint } from '../templates/zoneHitTest';
import type { RetroTemplate } from '../templates/types';

/**
 * Color emojis matching STICKY_COLORS index order:
 * 0=Yellow 🟡, 1=Rose 🔴, 2=Mint 🟢, 3=Sky 🔵, 4=Lavender 🟣, 5=Peach 🟠
 */
export const COLOR_EMOJIS = ['🟡', '🔴', '🟢', '🔵', '🟣', '🟠'] as const;

/**
 * Per-label DOM measurements (board pixels). Supplied by CanvasSurface at export
 * time from its `labelRefsRef` so the grouping algorithm uses the real rendered widths
 * rather than estimates.
 */
export type LabelDimensions = Map<string, { w: number; h: number }>;

/**
 * One section: a text-label heading (or null for ungrouped stickies), its sticky notes,
 * and any text labels positioned within the section that are not themselves headers
 * (e.g. a sentence written on the canvas below the section title).
 */
export interface ExportSection {
  label: TextLabelState | null;
  stickies: StickyState[];
  contentLabels: TextLabelState[];
  /** Template zone heading — used when label is null (template-based export). */
  zoneTitle?: string;
}

/**
 * Labels with fontSize at or above this threshold are section headers.
 * Labels below it are content notes attached to the nearest section.
 *
 * Matches DEFAULT_LABEL_FONT (48px) — every new label starts at this size.
 * Users explicitly shrink a label to create a sub-header or introductory note,
 * so anything at the default or above is treated as a prominent section title.
 */
export const HEADER_FONT_THRESHOLD = DEFAULT_LABEL_FONT;

/**
 * Outlier detection: a sticky is removed from its section and placed in "Other"
 * when its distance to the section header is more than this many times the mean
 * distance of its siblings. Prevents isolated stickies from being swept into the
 * nearest section just because no closer header exists.
 */
export const OUTLIER_DISTANCE_RATIO = 3.0;

/**
 * Minimum distance (board px) a sticky must be from its section header before the
 * outlier ratio check is applied. Avoids false positives in tightly-packed sections
 * where small absolute distances can still produce a high ratio.
 * Roughly 1.5× the default sticky size (200 px).
 */
export const OUTLIER_MIN_DISTANCE = 300;

/** Fallback width estimate when DOM measurements are unavailable. */
function estimateLabelWidth(label: TextLabelState): number {
  const fontSize = label.fontSize ?? DEFAULT_LABEL_FONT;
  return Math.max(150, label.content.length * fontSize * 0.6 + fontSize * 0.5);
}

function sortByYX<T extends { x: number; y: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
}

function colorEmoji(colorSlot: number): string {
  return (COLOR_EMOJIS[colorSlot] ?? '⬜') + ' ';
}

function sectionHeading(section: ExportSection): string {
  if (section.zoneTitle) return section.zoneTitle;
  return section.label ? (section.label.content.trim() || 'Untitled') : 'Other';
}

function sectionItemCount(s: ExportSection): number {
  return s.stickies.length + s.contentLabels.length;
}

interface Point { x: number; y: number }

/** Centre of a label's bounding box, using DOM dims when available. */
function labelCentre(label: TextLabelState, labelDims?: LabelDimensions): Point {
  const lw = labelDims?.get(label.id)?.w ?? estimateLabelWidth(label);
  const lh = labelDims?.get(label.id)?.h ?? 60;
  return { x: label.x + lw / 2, y: label.y + lh / 2 };
}

/** Euclidean distance between two points. */
function euclidean(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Classify text labels into section headers and content notes by font size.
 *
 * This is more robust than spatial classification because it works regardless
 * of whether the header sits above, below, or beside its stickies — the user
 * signals intent through font size, not position.
 */
function classifyLabelsByFontSize(labels: TextLabelState[]): {
  headers: TextLabelState[];
  contentNotes: TextLabelState[];
} {
  const headers: TextLabelState[] = [];
  const contentNotes: TextLabelState[] = [];
  for (const label of labels) {
    if ((label.fontSize ?? DEFAULT_LABEL_FONT) >= HEADER_FONT_THRESHOLD) {
      headers.push(label);
    } else {
      contentNotes.push(label);
    }
  }
  return { headers, contentNotes };
}

/** Find the nearest section header for a point by Euclidean centre-to-centre distance. */
function findNearestHeader(
  point: Point,
  headers: TextLabelState[],
  labelDims?: LabelDimensions,
): TextLabelState | null {
  let best: TextLabelState | null = null;
  let bestDist = Infinity;
  for (const header of headers) {
    const d = euclidean(point, labelCentre(header, labelDims));
    if (d < bestDist) {
      bestDist = d;
      best = header;
    }
  }
  return best;
}

/**
 * Spatial centroid of a section (header + its assigned stickies).
 *
 * Using the centroid rather than just the header position prevents content
 * notes that are sandwiched between two section headers from being pulled
 * toward the geometrically closer header when the actual cluster of stickies
 * for their section is on the other side.
 */
function computeSectionCentroid(
  header: TextLabelState,
  stickies: StickyState[],
  labelDims?: LabelDimensions,
): Point {
  const hc = labelCentre(header, labelDims);
  if (stickies.length === 0) return hc;
  const points: Point[] = [
    hc,
    ...stickies.map(s => ({ x: s.x + s.width / 2, y: s.y + s.height / 2 })),
  ];
  return {
    x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
    y: points.reduce((sum, p) => sum + p.y, 0) / points.length,
  };
}

/**
 * Remove distance outliers from each named section.
 *
 * For each section with ≥ 2 stickies, iteratively checks whether the most
 * distant sticky (measured from the section header) is an outlier:
 *
 *   distance > OUTLIER_DISTANCE_RATIO × mean(sibling distances)
 *   AND distance > OUTLIER_MIN_DISTANCE
 *
 * Detected outliers are stripped from the section so they can be placed in
 * the "Other" bucket. The loop repeats until no more outliers are found,
 * catching the case where multiple isolated stickies exist in one section.
 *
 * Sections with fewer than 2 stickies are left untouched — there is no
 * sibling baseline to compare against.
 */
function removeDistanceOutliers(
  sections: ExportSection[],
  labelDims?: LabelDimensions,
): { cleaned: ExportSection[]; outliers: StickyState[] } {
  const allOutliers: StickyState[] = [];

  const cleaned = sections.map(section => {
    if (!section.label || section.stickies.length < 2) return section;

    const hc = labelCentre(section.label, labelDims);
    let stickies = [...section.stickies];
    let found = true;

    while (found && stickies.length >= 2) {
      found = false;

      const withDist = stickies.map(s => ({
        sticky: s,
        d: euclidean({ x: s.x + s.width / 2, y: s.y + s.height / 2 }, hc),
      }));
      withDist.sort((a, b) => b.d - a.d); // farthest first

      const farthest = withDist[0];
      const rest = withDist.slice(1);
      const meanSiblingDist = rest.reduce((sum, r) => sum + r.d, 0) / rest.length;

      if (
        farthest.d >= OUTLIER_MIN_DISTANCE &&
        farthest.d > OUTLIER_DISTANCE_RATIO * meanSiblingDist
      ) {
        allOutliers.push(farthest.sticky);
        stickies = rest.map(r => r.sticky);
        found = true;
      }
    }

    return { ...section, stickies };
  });

  return { cleaned, outliers: allOutliers };
}

/**
 * Group stickies (and canvas text notes) under their spatially nearest section header.
 *
 * ALGORITHM — three phases:
 *
 * 1. CLASSIFY: labels with fontSize >= HEADER_FONT_THRESHOLD are section headers.
 *    Smaller labels are "content notes" (introductory text, sub-headings, etc.).
 *
 * 2. STICKY ASSIGNMENT: each sticky goes to the nearest section header by Euclidean
 *    centre-to-centre distance. No directional constraint — headers may sit above,
 *    below, or beside their stickies.
 *
 * 3. CONTENT NOTE ASSIGNMENT: content notes go to the section whose centroid
 *    (header + assigned stickies) is nearest. The centroid correctly pulls
 *    introductory labels toward the cluster they describe even when a different
 *    header happens to be the geometrically closer point.
 *
 * 4. OUTLIER REMOVAL: stickies that are far outliers within their section
 *    (distance > OUTLIER_DISTANCE_RATIO × mean sibling distance AND >
 *    OUTLIER_MIN_DISTANCE) are demoted to the "Other" section. This prevents
 *    isolated stickies from being swept into a distant section just because
 *    no closer header exists.
 *
 * Sections with no stickies AND no content notes are omitted.
 * Stickies on a board with no headers go into an "Other" section at the end.
 * Blank stickies (empty or whitespace-only) are excluded.
 */
export function groupStickiesByLabel(
  stickies: StickyState[],
  labels: TextLabelState[],
  labelDims?: LabelDimensions,
): ExportSection[] {
  const { headers, contentNotes } = classifyLabelsByFontSize(labels);

  // Stable section ordering: left-to-right by header x (formatters may re-sort by size).
  const sortedHeaders = [...headers].sort((a, b) => (a.x !== b.x ? a.x - b.x : a.y - b.y));

  // Phase 1: assign each sticky to the nearest header by Euclidean distance.
  const stickyMap = new Map<string, StickyState[]>();
  const uncategorised: StickyState[] = [];

  for (const sticky of stickies) {
    if (!sticky.content.trim()) continue;
    const centre: Point = { x: sticky.x + sticky.width / 2, y: sticky.y + sticky.height / 2 };
    const best = findNearestHeader(centre, headers, labelDims);
    if (best) {
      if (!stickyMap.has(best.id)) stickyMap.set(best.id, []);
      stickyMap.get(best.id)?.push(sticky);
    } else {
      uncategorised.push(sticky);
    }
  }

  // Phase 2: assign content notes to the section with the nearest centroid.
  const contentByHeader = new Map<string, TextLabelState[]>();
  if (contentNotes.length > 0 && headers.length > 0) {
    const centroids = new Map<string, Point>(
      headers.map(h => [h.id, computeSectionCentroid(h, stickyMap.get(h.id) ?? [], labelDims)]),
    );
    for (const note of contentNotes) {
      const nc = labelCentre(note, labelDims);
      let bestHeader: TextLabelState | null = null;
      let bestDist = Infinity;
      for (const header of headers) {
        const d = euclidean(nc, centroids.get(header.id) ?? labelCentre(header, labelDims));
        if (d < bestDist) {
          bestDist = d;
          bestHeader = header;
        }
      }
      if (bestHeader) {
        if (!contentByHeader.has(bestHeader.id)) contentByHeader.set(bestHeader.id, []);
        contentByHeader.get(bestHeader.id)?.push(note);
      }
    }
  }

  const sections: ExportSection[] = sortedHeaders
    .filter(h => (stickyMap.get(h.id)?.length ?? 0) > 0 || (contentByHeader.get(h.id)?.length ?? 0) > 0)
    .map(h => ({
      label: h,
      stickies: sortByYX(stickyMap.get(h.id) ?? []),
      contentLabels: sortByYX(contentByHeader.get(h.id) ?? []),
    }));

  if (uncategorised.length > 0) {
    sections.push({ label: null, stickies: sortByYX(uncategorised), contentLabels: [] });
  }

  // Phase 3: remove stickies that are distance outliers within their section.
  const { cleaned, outliers } = removeDistanceOutliers(sections, labelDims);
  if (outliers.length > 0) {
    const otherIdx = cleaned.findIndex(s => s.label === null);
    if (otherIdx >= 0) {
      cleaned[otherIdx] = {
        ...cleaned[otherIdx],
        stickies: sortByYX([...cleaned[otherIdx].stickies, ...outliers]),
      };
    } else {
      cleaned.push({ label: null, stickies: sortByYX(outliers), contentLabels: [] });
    }
  }

  return cleaned;
}

/**
 * Group stickies and text labels under template zones by centre-point hit-test.
 *
 * Stickies are placed in their zone's `stickies` list; text labels are placed in
 * `contentLabels` so they render with a `→` / `##` prefix interleaved by position —
 * the same behaviour as non-template (label-based) exports.
 *
 * Zones are only emitted when they contain at least one sticky. This means:
 *   - Partial (selection) exports only show zones with selected stickies.
 *   - Content labels in empty zones are silently dropped.
 *
 * Items outside all zones: stickies go to the uncategorised "Other" bucket;
 * out-of-zone labels go to Other's contentLabels.
 *
 * @param labelDims  Optional DOM-measured dimensions — improves hit-test accuracy
 *   because live labels are created with width/height = 0 and the rendered size
 *   only becomes known after layout.
 */
export function groupStickiesByZone(
  stickies: StickyState[],
  labels: TextLabelState[],
  template: RetroTemplate,
  labelDims?: LabelDimensions,
): ExportSection[] {
  const zoneMap = new Map<string, StickyState[]>();
  const zoneLabelMap = new Map<string, TextLabelState[]>();
  const uncategorised: StickyState[] = [];
  const uncategorisedLabels: TextLabelState[] = [];

  for (const sticky of stickies) {
    if (!sticky.content.trim()) continue;
    const centre: Point = {
      x: sticky.x + sticky.width / 2,
      y: sticky.y + sticky.height / 2,
    };
    const zone = findZoneForPoint(centre, template);
    if (zone) {
      if (!zoneMap.has(zone.id)) zoneMap.set(zone.id, []);
      zoneMap.get(zone.id)?.push(sticky);
    } else {
      uncategorised.push(sticky);
    }
  }

  // Use labelCentre (with DOM dims when available) so live labels with width/height = 0
  // are placed correctly instead of always using their top-left corner.
  for (const label of labels) {
    if (!label.content.trim()) continue;
    const centre = labelCentre(label, labelDims);
    const zone = findZoneForPoint(centre, template);
    if (zone) {
      if (!zoneLabelMap.has(zone.id)) zoneLabelMap.set(zone.id, []);
      zoneLabelMap.get(zone.id)?.push(label);
    } else {
      uncategorisedLabels.push(label);
    }
  }

  const sortedZones = [...template.zones].sort((a, b) => a.exportOrder - b.exportOrder);
  // Only emit sections that contain at least one sticky. This ensures:
  //   a) Partial exports (selection) only show zones with selected stickies.
  //   b) Content labels in zones without stickies don't generate phantom sections.
  const sections: ExportSection[] = sortedZones
    .filter(z => (zoneMap.get(z.id)?.length ?? 0) > 0)
    .map(z => ({
      label: null,
      zoneTitle: z.title,
      stickies: sortByYX(zoneMap.get(z.id) ?? []),
      contentLabels: sortByYX(zoneLabelMap.get(z.id) ?? []),
    }));

  if (uncategorised.length > 0 || uncategorisedLabels.length > 0) {
    sections.push({
      label: null,
      stickies: sortByYX(uncategorised),
      contentLabels: sortByYX(uncategorisedLabels),
    });
  }

  return sections;
}

/**
 * Choose zone-based or label-based grouping depending on the active template.
 * Returns sections in render-ready order:
 *   - Zone-based: sorted by exportOrder (template defines the canonical order).
 *   - Label-based: sorted by descending item count (sortBySize).
 */
export function groupForExport(
  stickies: StickyState[],
  labels: TextLabelState[],
  templateId: string | null | undefined,
  getTemplateFn: (id: string) => RetroTemplate | null,
  labelDims?: LabelDimensions,
): ExportSection[] {
  const template = templateId ? getTemplateFn(templateId) : null;
  if (template?.zones.length) {
    return groupStickiesByZone(stickies, labels, template, labelDims);
  }
  return sortBySize(groupStickiesByLabel(stickies, labels, labelDims));
}

/**
 * Sort sections by descending total item count (stickies + content labels).
 * The "Other" bucket (label === null) is always placed last regardless of size.
 */
export function sortBySize(sections: ExportSection[]): ExportSection[] {
  const isOther = (s: ExportSection) => s.label === null && !s.zoneTitle;
  const other = sections.filter(isOther);
  const named = sections.filter(s => !isOther(s));
  named.sort((a, b) => sectionItemCount(b) - sectionItemCount(a));
  return [...named, ...other];
}

/** Merge stickies and content labels into a single position-sorted sequence. */
type ExportItem = { kind: 'sticky'; item: StickyState } | { kind: 'label'; item: TextLabelState };

function interleaveByPosition(stickies: StickyState[], contentLabels: TextLabelState[]): ExportItem[] {
  const items: ExportItem[] = [
    ...stickies.map(s => ({ kind: 'sticky' as const, item: s })),
    ...contentLabels.map(l => ({ kind: 'label' as const, item: l })),
  ];
  return items.sort((a, b) => (a.item.y !== b.item.y ? a.item.y - b.item.y : a.item.x - b.item.x));
}

/**
 * Render pre-grouped, pre-sorted sections as GitHub-flavored Markdown.
 *
 * Expects sections already ordered by size (dominant first) — call sortBySize
 * before passing, or use the formatAsMarkdown convenience wrapper.
 *
 * Template (zone-based) exports use H1 (`#`) for every section — all zones
 * are top-level peers. Content labels within a zone render as H2 (`##`) sub-headings.
 *
 * For label-based exports the dominant section (first named, when there are multiple)
 * uses H1; the rest use H2. Content labels in those sections render with a `→` prefix.
 */
export function renderSectionsAsMarkdown(sections: ExportSection[]): string {
  const allZoneBased = sections.length > 0 && sections.every(s => !!s.zoneTitle);
  return sections
    .map((section, i) => {
      const { label, stickies: items, contentLabels, zoneTitle } = section;
      const isNamed = label !== null || !!zoneTitle;
      // Template zones are all H1 peers; label-based uses H1 only for the dominant section.
      const isDominant = !allZoneBased && i === 0 && isNamed && sections.length > 1;
      const hLevel = allZoneBased || isDominant ? '#' : '##';
      const heading = `${hLevel} ${sectionHeading(section)}`;

      const bullets = interleaveByPosition(items, contentLabels)
        .map(entry =>
          entry.kind === 'sticky'
            ? `- ${colorEmoji(entry.item.colorSlot)}${entry.item.content.trim()}`
            // Zone sections use ## for sub-headings; label-based sections use →.
            : allZoneBased
              ? `## ${entry.item.content.trim()}`
              : `→ ${entry.item.content.trim()}`,
        )
        .join('\n');

      return `${heading}\n${bullets}`;
    })
    .join('\n\n');
}

/**
 * Render pre-grouped, pre-sorted sections as plain text.
 *
 * Expects sections already ordered by size — call sortBySize before passing,
 * or use the formatAsPlainText convenience wrapper.
 *
 * Headings are uppercased; stickies use `•`; content labels use `→`.
 * No color emojis — plain text targets environments where emojis are unwanted.
 */
export function renderSectionsAsPlainText(sections: ExportSection[]): string {
  return sections
    .map((section) => {
      const { stickies: items, contentLabels } = section;
      const heading = sectionHeading(section).toUpperCase();

      const bullets = interleaveByPosition(items, contentLabels)
        .map(entry =>
          entry.kind === 'sticky'
            ? `• ${entry.item.content.trim()}`
            : `→ ${entry.item.content.trim()}`,
        )
        .join('\n');

      return `${heading}\n${bullets}`;
    })
    .join('\n\n');
}

/**
 * Group and render stickies as GitHub-flavored Markdown in one call.
 * Prefer calling groupStickiesByLabel + renderSectionsAsMarkdown separately
 * when you also need the section count (avoids grouping twice).
 */
export function formatAsMarkdown(
  stickies: StickyState[],
  labels: TextLabelState[],
  labelDims?: LabelDimensions,
): string {
  return renderSectionsAsMarkdown(sortBySize(groupStickiesByLabel(stickies, labels, labelDims)));
}

/**
 * Group and render stickies as plain text in one call.
 * Prefer calling groupStickiesByLabel + renderSectionsAsPlainText separately
 * when you also need the section count (avoids grouping twice).
 */
export function formatAsPlainText(
  stickies: StickyState[],
  labels: TextLabelState[],
  labelDims?: LabelDimensions,
): string {
  return renderSectionsAsPlainText(sortBySize(groupStickiesByLabel(stickies, labels, labelDims)));
}

/** Total number of items (stickies + content labels) across all sections. */
export function countExportItems(sections: ExportSection[]): number {
  return sections.reduce((total, s) => total + sectionItemCount(s), 0);
}
